import React, { useState, useEffect, useRef } from "react";
import {
  ChatMessage as ChatMessageType,
  ChatSession,
  ImageAttachment,
} from "../types";
import { apiService } from "../services/api";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import SessionManager from "./SessionManager";
import ThemeToggle from "./ThemeToggle";
import UserMenu from "./UserMenu";
import { useAuth } from "../contexts/AuthContext";
import { Bot, AlertCircle } from "lucide-react";

interface SessionTitle {
  [sessionId: string]: string;
}

// Helper function to generate conversation titles
const generateConversationTitle = (content: string): string => {
  // Clean and truncate the content
  const cleanContent = content.trim().replace(/[^\w\s]/g, "");

  // If it's a question, extract the main topic
  if (
    cleanContent.toLowerCase().startsWith("what") ||
    cleanContent.toLowerCase().startsWith("how") ||
    cleanContent.toLowerCase().startsWith("why") ||
    cleanContent.toLowerCase().startsWith("when") ||
    cleanContent.toLowerCase().startsWith("where") ||
    cleanContent.toLowerCase().startsWith("can") ||
    cleanContent.toLowerCase().startsWith("could") ||
    cleanContent.toLowerCase().startsWith("would")
  ) {
    // Extract key words (remove common words)
    const words = cleanContent
      .toLowerCase()
      .split(" ")
      .filter(
        (word) =>
          word.length > 3 &&
          ![
            "what",
            "how",
            "why",
            "when",
            "where",
            "can",
            "could",
            "would",
            "about",
            "with",
            "from",
            "this",
            "that",
            "they",
            "them",
            "their",
            "there",
            "these",
            "those",
          ].includes(word)
      );

    if (words.length > 0) {
      return words
        .slice(0, 3)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
  }

  // For other types of messages, take first few meaningful words
  const words = cleanContent
    .split(" ")
    .filter(
      (word) =>
        word.length > 2 &&
        ![
          "the",
          "and",
          "or",
          "but",
          "in",
          "on",
          "at",
          "to",
          "for",
          "of",
          "with",
          "by",
          "is",
          "are",
          "was",
          "were",
          "be",
          "been",
          "have",
          "has",
          "had",
          "do",
          "does",
          "did",
          "will",
          "would",
          "could",
          "should",
        ].includes(word.toLowerCase())
    );

  if (words.length > 0) {
    return words.slice(0, 4).join(" ");
  }

  // Fallback to truncated original
  return content.length > 30 ? content.substring(0, 30) + "..." : content;
};

const getThinkingMessage = (status: string): string => {
  if (!status) return "AI is thinking";

  // Convert backend status messages to user-friendly thinking messages
  const statusMap: { [key: string]: string } = {
    processing: "AI is analyzing",
    searching: "AI is searching",
    thinking: "AI is thinking",
    analyzing: "AI is analyzing",
    retrieving: "AI is gathering information",
    generating: "AI is formulating response",
    submitting: "AI is processing",
    pending: "AI is thinking",
    in_progress: "AI is working",
    "submitting question...": "AI is processing",
  };

  // Check for exact matches first
  if (statusMap[status.toLowerCase()]) {
    return statusMap[status.toLowerCase()];
  }

  // Check for partial matches
  const lowerStatus = status.toLowerCase();
  if (lowerStatus.includes("submit")) return "AI is processing";
  if (lowerStatus.includes("search")) return "AI is searching";
  if (lowerStatus.includes("analyz")) return "AI is analyzing";
  if (lowerStatus.includes("process")) return "AI is processing";
  if (lowerStatus.includes("generat")) return "AI is formulating response";
  if (lowerStatus.includes("retriev")) return "AI is gathering information";

  // Default fallback
  return "AI is thinking";
};

const getSubtitleMessage = (status: string): string => {
  if (!status) return "Analyzing your question and searching knowledge base";

  const lowerStatus = status.toLowerCase();

  if (lowerStatus.includes("submit")) return "Processing your request";
  if (lowerStatus.includes("search")) return "Searching through knowledge base";
  if (lowerStatus.includes("analyz"))
    return "Analyzing context and information";
  if (lowerStatus.includes("process")) return "Processing your request";
  if (lowerStatus.includes("generat")) return "Crafting the perfect response";
  if (lowerStatus.includes("retriev")) return "Gathering relevant information";
  if (lowerStatus.includes("thinking")) return "Considering the best approach";

  return "Working on your question";
};

// Helper to correctly parse server timestamps (UTC without timezone info)
const parseServerDate = (isoString?: string | null): Date => {
  if (!isoString) return new Date();

  // If the string already includes timezone information, use it directly
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(isoString)) {
    return new Date(isoString);
  }

  // Backend sends naive UTC (e.g. "2025-12-02T10:00:00"),
  // so append "Z" to interpret it as UTC in the browser
  return new Date(`${isoString}Z`);
};

const ChatInterface: React.FC = () => {
  const { user } = useAuth(); // Get current user for thread ID generation
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setProgress] = useState(0);
  const [status, setStatus] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(
    null
  );

  // Session management state
  const [currentSessionId, setCurrentSessionId] = useState<string>("");
  const [sessionTitles, setSessionTitles] = useState<SessionTitle>({});
  const [sessionsVisible, setSessionsVisible] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [newSession, setNewSession] = useState<ChatSession | undefined>(
    undefined
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check backend health on component mount
  useEffect(() => {
    checkBackendHealth();
  }, []);

  // Save current session to localStorage whenever it changes
  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem("current_session_id", currentSessionId);
    } else {
      localStorage.removeItem("current_session_id");
    }
  }, [currentSessionId]);

  const checkBackendHealth = async () => {
    try {
      await apiService.checkHealth();
      setIsConnected(true);
      setError(null);
    } catch (error) {
      console.error("Backend health check failed:", error);
      setIsConnected(false);
      setError(
        "Unable to connect to the backend. Please check your connection."
      );
    }
  };

  const handleRetryConnection = async () => {
    setError(null);
    await checkBackendHealth();
  };

  const handleRetryLastMessage = async () => {
    if (lastFailedMessage) {
      setError(null);
      setRetryCount(0);
      await handleSendMessage(lastFailedMessage);
    }
  };

  const handleSendMessage = async (content: string, image?: File | null) => {
    if ((!content.trim() && !image) || isLoading) return;

    // Create new session if none exists
    let sessionId = currentSessionId;
    let isNewSession = false;
    if (!sessionId) {
      isNewSession = true;
      // Generate a unique thread ID with user context
      const timestamp = Date.now();
      const userPrefix = user?.id ? user.id.slice(-8) : "anon";
      sessionId = `thread_${userPrefix}_${timestamp}`;
      setCurrentSessionId(sessionId);
      // Trigger refresh of SessionManager to show new session
      setRefreshTrigger((prev) => prev + 1);
    }

    // Create temporary image attachment for optimistic display
    let tempImageAttachments: ImageAttachment[] | undefined = undefined;
    if (image) {
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(image);
      tempImageAttachments = [
        {
          filename: image.name || `pasted-image-${Date.now()}.png`,
          content_type: image.type || "image/png",
          file_path: previewUrl, // Use blob URL for preview
          size: image.size,
          uploaded_at: new Date().toISOString(),
        },
      ];
    }

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      content: content.trim(),
      role: "user",
      timestamp: new Date(),
      thread_id: sessionId,
      image_attachments: tempImageAttachments,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    // If it's a new session, create a meaningful title from the first message
    if (isNewSession) {
      const title = generateConversationTitle(content.trim());
      setSessionTitles((prev) => ({
        ...prev,
        [sessionId]: title,
      }));

      // Create new session object for SessionManager
      const newSessionObj: ChatSession = {
        id: sessionId,
        name: title,
        created_at: new Date(),
        last_message_at: new Date(),
        message_count: 1,
      };
      setNewSession(newSessionObj);

      // Update localStorage with the new session name
      const savedSessions = localStorage.getItem("chat_sessions");
      if (savedSessions) {
        try {
          const sessions = JSON.parse(savedSessions);
          const updatedSessions = sessions.map((session: ChatSession) =>
            session.id === sessionId ? { ...session, name: title } : session
          );
          localStorage.setItem(
            "chat_sessions",
            JSON.stringify(updatedSessions)
          );
        } catch (error) {
          console.error("Error updating session name in localStorage:", error);
        }
      }
    }

    try {
      // Reset progress and status
      setProgress(0);
      setStatus("Submitting question...");

      // Use async polling to avoid timeout errors
      const response = await apiService.askQuestionAsync(
        content,
        false,
        sessionId,
        (progressValue) => {
          setProgress(progressValue);
        },
        (statusValue) => {
          setStatus(statusValue);
        },
        image || null
      );

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        content: response.answer,
        role: "assistant",
        timestamp: new Date(),
        confidence_score: response.confidence_score,
        thread_id: response.thread_id || sessionId,
      };

      // Update messages with assistant response
      setMessages((prev) => [...prev, assistantMessage]);

      // After receiving response, reload conversation history to get updated messages with correct image paths
      // This replaces the optimistic message with the server version that has proper image_attachments
      // and prevents duplicates
      try {
        // Capture current messages state before reloading to preserve temporary blob URLs
        const currentMessages = [...messages, assistantMessage];

        const historyResponse = await apiService.getConversationHistory(
          sessionId
        );

        // Create a map of temporary blob URLs from current messages to preserve them
        const tempImageMap = new Map<string, ImageAttachment[]>();
        currentMessages.forEach((msg) => {
          if (msg.image_attachments && msg.image_attachments.length > 0) {
            // Check if any attachment is a blob URL (temporary)
            const hasBlobUrl = msg.image_attachments.some(
              (img) =>
                img.file_path.startsWith("blob:") ||
                img.file_path.startsWith("data:")
            );
            if (hasBlobUrl) {
              // Use content as key to match messages (more reliable than timestamp)
              const key = msg.content.trim();
              tempImageMap.set(key, msg.image_attachments);
            }
          }
        });

        const chatMessages: ChatMessageType[] = historyResponse.messages.map(
          (msg, index) => {
            // Try to match with temporary image attachments by content
            const key = msg.content.trim();
            const tempAttachments = tempImageMap.get(key);

            // Prefer server image_attachments, but fall back to temporary if server doesn't have them
            const imageAttachments =
              msg.image_attachments && msg.image_attachments.length > 0
                ? msg.image_attachments
                : tempAttachments;

            return {
              id: msg.id || `${sessionId}-${index}`,
              content: msg.content,
              role: msg.role as "user" | "assistant",
              timestamp: parseServerDate(msg.timestamp),
              confidence_score: msg.confidence_score,
              thread_id: sessionId,
              image_attachments: imageAttachments,
            };
          }
        );
        setMessages(chatMessages);
      } catch (error) {
        console.error("Error reloading conversation history:", error);
        // If reload fails, keep the current messages
      }

      // Update session activity
      if ((window as any).updateSessionActivity) {
        (window as any).updateSessionActivity(sessionId);
      }

      // If this was a new session, trigger another refresh to ensure it appears in sidebar
      if (isNewSession) {
        setRefreshTrigger((prev) => prev + 1);
        // Clear the newSession state since it's now been processed
        setNewSession(undefined);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setLastFailedMessage(content.trim());
      setRetryCount((prev) => prev + 1);

      // Determine error message based on error type
      let errorMessage = "Failed to send message. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes("timeout")) {
          errorMessage =
            "Request timed out. The server is taking longer than expected.";
        } else if (error.message.includes("Network Error")) {
          errorMessage =
            "Network connection error. Please check your internet connection.";
        } else if (error.message.includes("Failed to get response")) {
          errorMessage =
            "Unable to get response from the server. Please try again.";
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setProgress(0);
      setStatus("");
    }
  };

  // Session management functions
  const handleSessionChange = async (sessionId: string) => {
    setCurrentSessionId(sessionId);
    setError(null);
    setLoadingHistory(true);

    // If sessionId is empty, just clear messages and return
    if (!sessionId || sessionId.trim() === "") {
      setMessages([]);
      setLoadingHistory(false);
      return;
    }

    // Load conversation history for the selected session
    try {
      const historyResponse = await apiService.getConversationHistory(
        sessionId
      );

      // Convert conversation messages to ChatMessage format
      const chatMessages: ChatMessageType[] = historyResponse.messages.map(
        (msg, index) => ({
          id: msg.id || `${sessionId}-${index}`, // Use actual message ID from API
          content: msg.content,
          role: msg.role as "user" | "assistant",
          timestamp: new Date(msg.timestamp),
          confidence_score: msg.confidence_score,
          thread_id: sessionId,
          image_attachments: msg.image_attachments,
        })
      );

      setMessages(chatMessages);
    } catch (error) {
      console.error("Error loading conversation history:", error);
      // If there's an error loading history, start with empty messages
      setMessages([]);

      // Check if it's a 400 error (empty thread ID) or 404 (thread not found)
      if (
        error instanceof Error &&
        error.message.includes("Thread ID cannot be empty")
      ) {
        // This happens when a deleted conversation is selected - just clear silently
        setError(null);
      } else {
        setError("Failed to load conversation history");
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleNewSession = () => {
    setMessages([]);
    setError(null);
    setCurrentSessionId(""); // Clear current session to start fresh
    localStorage.removeItem("current_session_id"); // Clear saved session
  };

  // Clear session handler - COMMENTED OUT (not needed)
  // const handleClearSession = async (sessionId: string) => {
  //   try {
  //     await apiService.clearConversationHistory(sessionId);
  //     if (sessionId === currentSessionId) {
  //       setMessages([]);
  //     }
  //   } catch (error) {
  //     console.error("Error clearing session:", error);
  //     setError("Failed to clear conversation history");
  //   }
  // };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await apiService.deleteConversation(sessionId);
      if (sessionId === currentSessionId) {
        setMessages([]);
        setCurrentSessionId(""); // Clear session instead of setting to default
        localStorage.removeItem("current_session_id"); // Clear saved session
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      setError("Failed to delete conversation");
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 overflow-hidden">
      {/* Sidebar with Session Manager and Ticket Search */}
      <div
        className={`flex flex-col transition-all duration-300 ${
          sessionsVisible ? "w-64" : "w-16"
        } overflow-hidden relative`}
      >
        {/* Session Manager */}
        <div className="flex-1 min-h-0">
          <SessionManager
            currentSessionId={currentSessionId}
            sessionTitles={sessionTitles}
            onSessionChange={handleSessionChange}
            onNewSession={handleNewSession}
            onDeleteSession={handleDeleteSession}
            isCollapsed={!sessionsVisible}
            toggleSidebar={() => setSessionsVisible(!sessionsVisible)}
            refreshTrigger={refreshTrigger}
            newSession={newSession}
          />
        </div>

        {/* Ticket Search Widget - HIDDEN FOR NOW */}
        {/* {sessionsVisible && user && (
          <div className="flex-shrink-0 p-4 border-t border-orange-200/50 dark:border-neutral-700/50 bg-white/50 dark:bg-neutral-800/50">
            <TicketSearchWidget
              onTicketSelect={handleTicketSelect}
              embedded={true}
            />
          </div>
        )} */}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="flex-shrink-0 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-xl border-b border-orange-200/50 dark:border-neutral-700/50 px-4 py-3 relative z-10">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                <Bot size={18} className="text-white" />
              </div>
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 leading-tight truncate">
                  3PM Construction Agent
                </h1>
                <p className="text-xs text-orange-600 dark:text-orange-400 font-medium leading-tight">
                  AI Construction Assistant
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* User Menu */}
              <UserMenu />

              {/* Theme Toggle */}
              <ThemeToggle variant="tab" size="sm" />
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="flex-shrink-0 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-b border-red-200 dark:border-red-800 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertCircle
                    size={16}
                    className="text-red-600 dark:text-red-400"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-red-800 dark:text-red-200">
                    {error}
                  </span>
                  {retryCount > 0 && (
                    <span className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Retry attempt: {retryCount}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {lastFailedMessage && (
                  <button
                    onClick={handleRetryLastMessage}
                    disabled={isLoading}
                    className="px-4 py-2 bg-orange-600 dark:bg-orange-700 text-white text-sm font-medium rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Retry Message
                  </button>
                )}
                <button
                  onClick={handleRetryConnection}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-sm font-medium rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-all duration-200"
                >
                  Retry Connection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loadingHistory ? (
            <div className="flex justify-center items-center h-full">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 dark:from-neutral-800/50 dark:to-neutral-700/50 rounded-2xl">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center animate-pulse">
                  <Bot size={16} className="text-white" />
                </div>
                <div>
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium">
                    Loading conversation history...
                  </span>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Retrieving messages
                  </div>
                </div>
              </div>
            </div>
          ) : messages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
              <div className="text-center max-w-4xl px-4 w-full">
                {/* Hero Section - More Compact */}
                <div className="mb-6 animate-fade-in-up">
                  <div className="relative mb-4">
                    <div className="w-12 h-12 mx-auto bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Bot size={20} className="text-white" />
                    </div>
                  </div>

                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-3 gradient-text-primary leading-tight">
                    Welcome to 3PM Construction Agent
                  </h1>
                  <p className="text-base text-neutral-600 dark:text-neutral-300 mb-6 leading-relaxed max-w-3xl mx-auto text-balance">
                    Your intelligent construction companion for project
                    management, safety protocols, and workflow optimization. Get
                    instant answers about construction processes, safety
                    guidelines, and project workflows with AI-powered insights.
                  </p>
                </div>

                {/* Feature Cards - More Compact */}
                {/* 
                <div
                  className="grid md:grid-cols-3 gap-4 mb-8 animate-fade-in-up"
                  style={{ animationDelay: "0.2s" }}
                >
                  <div
                    className="card-hover p-6 group cursor-pointer"
                    onClick={() =>
                      handleSendMessage(
                        "What are the safety protocols for scaffolding?"
                      )
                    }
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <HardHat size={20} className="text-orange-600" />
                    </div>
                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      Safety Management
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">
                      Access safety protocols and compliance guidelines
                    </p>
                    <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                      Try: "What are the safety protocols for scaffolding?"
                    </div>
                  </div>

                  <div
                    className="card-hover p-6 group cursor-pointer"
                    onClick={() =>
                      handleSendMessage("How do I track project progress?")
                    }
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Building size={20} className="text-yellow-600" />
                    </div>
                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      Project Tracking
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">
                      Monitor construction progress and milestones
                    </p>
                    <div className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                      Try: "How do I track project progress?"
                    </div>
                  </div>

                  <div
                    className="card-hover p-6 group cursor-pointer"
                    onClick={() =>
                      handleSendMessage(
                        "What tools are needed for electrical work?"
                      )
                    }
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Wrench size={20} className="text-red-600" />
                    </div>
                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      Equipment & Tools
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">
                      Get tool recommendations and equipment lists
                    </p>
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Try: "What tools are needed for electrical work?"
                    </div>
                  </div>
                </div>
                */}

                {/* Sample Questions - More Compact */}
                {/* 
                <div
                  className="card p-6 animate-fade-in-up"
                  style={{ animationDelay: "0.4s" }}
                >
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                    Quick Start Questions
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      "What are the safety protocols for scaffolding?",
                      "How do I track project progress?",
                      "What tools are needed for electrical work?",
                      "Show me construction workflow components",
                      "Explain the concrete pouring process",
                      "Find all safety equipment requirements",
                    ].map((question, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gradient-to-r from-neutral-50 to-neutral-100/50 dark:from-neutral-800/50 dark:to-neutral-700/50 rounded-lg hover:from-orange-50 hover:to-orange-100/50 dark:hover:from-orange-900/20 dark:hover:to-orange-800/20 transition-all duration-200 cursor-pointer group"
                        onClick={() => handleSendMessage(question)}
                      >
                        <span className="text-sm text-neutral-700 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-100 transition-colors flex-1">
                          {question}
                        </span>
                        <ArrowRight
                          size={12}
                          className="text-neutral-400 group-hover:text-orange-500 transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                */}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message, index) => (
                <ChatMessage key={message.id} message={message} />
              ))}
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start animate-fade-in">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 dark:from-neutral-800/50 dark:to-neutral-700/50 rounded-2xl">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                  <Bot size={16} className="text-white animate-pulse" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-700 dark:text-neutral-300 font-medium">
                      {getThinkingMessage(status)}
                    </span>
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full thinking-dot"></div>
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full thinking-dot"></div>
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full thinking-dot"></div>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {getSubtitleMessage(status)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input */}
        <div className="flex-shrink-0 p-2">
          <ChatInput
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            disabled={!isConnected}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
